"use client";

import React from "react";
import { CircleCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
const practiceAreas = [{
  id: 1,
  title: "Full Home Inspections",
  description: "Comprehensive evaluation of your Homestead property's structural integrity, electrical systems, and plumbing. We protect your investment for your family with meticulous attention to detail."
}, {
  id: 2,
  title: "Wind Mitigation & 4-Point",
  description: "Specialized assessments for South Florida properties. Our wind mitigation and 4-point inspections document visible and accessible components at the time of inspection to support insurance carrier requirements. Does not guarantee insurance coverage, discounts, or carrier approval."
}, {
  id: 3,
  title: "Mold & Air Quality Testing",
  description: "Our licensed mold assessor uses professional sampling equipment to collect air and surface samples for mold analysis, with samples submitted to an independent laboratory when applicable."
}, {
  id: 4,
  title: "Additional Inspection Services",
  description: "Comprehensive specialized inspections tailored to South Florida properties, covering insurance, environmental, and pre-sale needs."
}];
const practiceHighlights = [{
  areaId: 1,
  text: "Structural Foundation"
}, {
  areaId: 1,
  text: "Moisture Detection\n"
}, {
  areaId: 1,
  text: "Plumbing Systems"
}, {
  areaId: 1,
  text: "HVAC Efficiency"
}, {
  areaId: 2,
  text: "Wind-Resistant Ties"
}, {
  areaId: 2,
  text: "Roof Deck Attachment"
}, {
  areaId: 2,
  text: "Water Heater Age"
}, {
  areaId: 2,
  text: "Secondary Water Barrier"
}, {
  areaId: 3,
  text: "Spore Count Analysis"
}, {
  areaId: 3,
  text: "Moisture Mapping"
}, {
  areaId: 3,
  text: "Surface Sampling"
}, {
  areaId: 4,
  text: "Wind Mitigation",
  link: "/WindMitigationInspectionPage"
}, {
  areaId: 4,
  text: "4-Point Inspection",
  link: "/FourPointInspectionSouthFloridaPage"
}, {
  areaId: 4,
  text: "Condo Inspection",
  link: "/CondoInspectionPage"
}, {
  areaId: 4,
  text: "Mold Testing",
  link: "/MoldTestingPage"
}, {
  areaId: 4,
  text: "Sewer Camera Inspection",
  link: "/SewerCameraInspectionPage"
}, {
  areaId: 4,
  text: "Pre-Listing Inspection",
  link: "/PreListingInspectionPage"
}];
function PracticeAreaCard({
  area,
  index
}: {
  area: {
    id: number;
    title: string;
    description: React.ReactNode;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const highlights = practiceHighlights.filter(h => h.areaId === area.id);
  return <div data-section-id="151327" ref={ref} data-index={index} className={cn("w-full transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      <Card className="group bg-card border border-border hover:border-primary/50 transition-colors duration-500 rounded-xl shadow-sm hover:shadow-md overflow-hidden">
        <CardContent className="p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="md:col-span-4">
            <h3 className="font-sans text-2xl md:text-3xl font-bold text-secondary group-hover:text-primary transition-colors duration-300">
              {area.title}
            </h3>
          </div>
          <div className="md:col-span-8 flex flex-col gap-8">
            <div className="font-sans text-muted-foreground text-lg leading-relaxed">
              {area.description}
            </div>
            
            {highlights.length > 0 && <div className="pt-6 border-t border-border/50">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {highlights.map((highlight, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                      <CircleCheck className="size-5 text-primary shrink-0 mt-0.5" />
                      {highlight.link ? <Link to={highlight.link} className="font-sans text-foreground font-medium hover:text-primary transition-colors underline-offset-4 hover:underline">
                          {highlight.text}
                        </Link> : <span className="font-sans text-foreground font-medium">
                          {highlight.text}
                        </span>}
                    </li>)}
                </ul>
              </div>}
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function PracticeAreas() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="services-offered-in-homestead" className="py-[128px] bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div ref={headerRef} className={cn("mb-16 md:mb-24 text-center transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-sans text-4xl md:text-5xl font-bold text-secondary mb-6">Home Inspection Services in Homestead</h2>
          <p className="font-sans text-lg text-muted-foreground max-w-2xl mx-auto">Providing precision-driven home inspections in Homestead, ensuring your real estate investments are protected by professional insight.</p>
        </div>

        <div className="flex flex-col gap-8">
          {practiceAreas.map((area, index) => <PracticeAreaCard key={area.id} area={area} index={index} />)}
        </div>
      </div>
    </section>;
}